import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Flame, Snowflake, CookingPot, Cake,
  ArrowLeft, Check, Minus, Plus,
} from 'lucide-react';
import { Trans, Plural } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ordersApi } from '@/lib/api/services/orders.api';
import { userApi } from '@/lib/api/services/user.api';
import { cookingSessionKeys } from '@/hooks/cooking-sessions/keys';
import { orderKeys } from '@/hooks/orders/keys';
import type { User } from '@/types/user';
import { cn } from '@/lib/utils';
import { CartLines } from './CartLines';
import { PrioPill } from '@/components/orders/board/OrderPrimitives';
import type { useOrderState } from './useOrderState';
import type { DeckPriority } from '@/lib/api/services/orders.api';

type OrderState = ReturnType<typeof useOrderState>;

interface Props {
  state: OrderState;
  locationId: number;
  menuId?: number;
  onBack: () => void;
}

type StationDef = { key: string; label: string; icon: React.ComponentType<{ className?: string }>; colorClass: string };
type PriorityDef = { value: DeckPriority; label: string };

export function Step3AssignSubmit({ state, locationId, menuId, onBack }: Props) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { assignment, patchAssignment, customer } = state;

  const STATIONS: StationDef[] = [
    { key: 'Grill',  label: t`Grill`,  icon: Flame,      colorClass: 'text-(--color-order-cooking-fg)' },
    { key: 'Cold',   label: t`Cold`,   icon: Snowflake,   colorClass: 'text-(--color-order-pickup-fg)'  },
    { key: 'Pasta',  label: t`Pasta`,  icon: CookingPot,  colorClass: 'text-(--color-text-primary)'                 },
    { key: 'Pastry', label: t`Pastry`, icon: Cake,        colorClass: 'text-(--color-text-primary)'                 },
  ];
  const PRIORITIES: PriorityDef[] = [
    { value: 'LOW',    label: t`Low`    },
    { value: 'NORMAL', label: t`Normal` },
    { value: 'HIGH',   label: t`High`   },
  ];

  // Fetch chef list
  const { data: usersData, isLoading: loadingChefs } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => userApi.getAll(),
  });
  const chefs: User[] = usersData ?? [];

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: () =>
      ordersApi.create({
        locationId,
        menuId,
        tableId: customer.tableId ?? undefined,
        serviceType: customer.serviceType,
        items: state.cart.map((l) => ({ menuItemId: l.menuItem.id, qty: l.qty })),
        guestName: customer.guestName || undefined,
        phone: customer.phone || undefined,
        guestCount: customer.guestCount,
        serverId: customer.serverId ?? undefined,
        leadChefId: assignment.leadChefId ?? undefined,
        allergens: customer.allergens,
        reservationTag: customer.reservationTag || undefined,
        kitchenNotes: customer.kitchenNotes || undefined,
        priority: assignment.priority,
        targetMinutes: assignment.targetMinutes,
        printChit: assignment.printChit,
        notifyAllergen: assignment.notifyAllergen,
      }),
    onSuccess: (order) => {
      const orderId = order.id;
      toast.success(
        t`Ticket sent to the line — ${state.itemCount} items`,
        { description: t`Order #${orderId}` },
      );
      void queryClient.invalidateQueries({ queryKey: cookingSessionKeys.all });
      void queryClient.invalidateQueries({ queryKey: orderKeys.all });
      void navigate({ to: '/working' });
    },
    onError: (error) => {
      toast.error(t`Failed to submit order`, {
        description: error instanceof Error ? error.message : t`Please try again.`,
      });
    },
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_25rem] gap-6 items-start">
      {/* ── Left ── */}
      <div className="space-y-5">
        {/* Routing */}
        <div className="rounded-2xl border border-(--color-border) bg-(--color-surface-content) p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[1.125rem] font-bold"><Trans>Routing</Trans></div>
              <p className="text-[0.8125rem] text-(--color-text-secondary)">
                <Trans>Items auto-route to stations; override below if needed.</Trans>
              </p>
            </div>
            <Button variant="ghost" size="sm" className="text-[0.8125rem]">
              <Trans>Reset</Trans>
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {STATIONS.map(({ key, label, icon: Icon, colorClass }) => {
              const count = state.cart.filter(
                (l) => l.menuItem.station?.toLowerCase() === key.toLowerCase(),
              ).length;
              return (
                <div
                  key={key}
                  className="flex flex-col gap-2 p-3.5 border border-(--color-border) rounded-[0.85rem] bg-(--color-surface-content)"
                >
                  <Icon className={cn('w-5 h-5', colorClass)} />
                  <div className="text-[1rem] font-bold text-(--color-text-primary)">{label}</div>
                  <div className="text-[0.75rem] text-(--color-text-secondary)">
                    {count > 0 ? (
                      <Plural value={count} one="# item" other="# items" />
                    ) : (
                      <Trans>Idle</Trans>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Lead chef */}
        <div className="rounded-2xl border border-(--color-border) bg-(--color-surface-content) p-5">
          <div className="text-[1.125rem] font-bold mb-1"><Trans>Lead chef</Trans></div>
          <p className="text-[0.8125rem] text-(--color-text-secondary) mb-4">
            <Trans>On-shift cooks — pick one to assign this ticket.</Trans>
          </p>
          {loadingChefs ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[1, 2, 3, 4].map((n) => <Skeleton key={n} className="h-16 rounded-[0.85rem]" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {chefs.map((chef) => {
                const isActive = assignment.leadChefId === Number(chef.id);
                return (
                  <button
                    key={chef.id}
                    type="button"
                    onClick={() =>
                      patchAssignment({
                        leadChefId: Number(chef.id),
                        leadChefName: `${chef.firstName ?? ''} ${chef.lastName ?? ''}`.trim(),
                      })
                    }
                    className={cn(
                      'flex items-center gap-3 p-3.5 border rounded-[0.85rem] bg-(--color-surface-content) text-left transition-all',
                      isActive
                        ? 'border-(--color-text-primary) ring-1 ring-foreground bg-(--color-surface-muted)/30'
                        : 'border-(--color-border) hover:border-muted-foreground',
                    )}
                  >
                    <div className="w-10 h-10 rounded-full bg-(--color-surface-muted) overflow-hidden shrink-0">
                      {chef.profileImageUrl && (
                        <img
                          src={chef.profileImageUrl}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[0.875rem] font-semibold truncate">
                        {chef.firstName} {chef.lastName}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Priority & timing */}
        {(() => {
          const maxTime = Math.max(0, ...state.cart.map((l) => l.menuItem.prepTimeMinutes));
          const suggestedMinutes = maxTime > 0 ? maxTime : 0;

          return (
            <div className="rounded-2xl border border-(--color-border) bg-(--color-surface-content) p-5">
              <div className="text-[1.125rem] font-bold mb-1"><Trans>Priority &amp; timing</Trans></div>
              <p className="text-[0.8125rem] text-(--color-text-secondary) mb-4">
                <Trans>How urgent is this ticket?</Trans>
              </p>

              <div className="space-y-4">
                {/* Priority */}
                <div className="space-y-1.5">
                  <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide">
                    <Trans>Priority</Trans>
                  </Label>
                  <div className="flex items-center gap-2">
                    {PRIORITIES.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => patchAssignment({ priority: value })}
                        className={cn(
                          'flex-1 h-11 rounded-[0.55rem] border font-semibold text-[0.84375rem] transition-colors',
                          assignment.priority === value
                            ? 'bg-(--color-text-primary) text-(--color-surface-page) border-(--color-text-primary)'
                            : 'bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) hover:bg-(--color-surface-muted)',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Per-item timing breakdown */}
                {state.cart.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide">
                        <Trans>Item timings</Trans>
                      </Label>
                      {suggestedMinutes > 0 && suggestedMinutes !== assignment.targetMinutes && (
                        <button
                          type="button"
                          onClick={() => patchAssignment({ targetMinutes: suggestedMinutes })}
                          className="text-[0.75rem] font-semibold text-(--color-text-primary) hover:underline"
                        >
                          <Trans>Use longest ({suggestedMinutes} min) ↑</Trans>
                        </button>
                      )}
                    </div>
                    <div className="rounded-[0.85rem] border border-(--color-border) divide-y divide-border overflow-hidden">
                      {state.cart.map(({ menuItem, qty }) => {
                        const itemTime = menuItem.prepTimeMinutes;
                        const pct = maxTime > 0 ? Math.round((itemTime / maxTime) * 100) : 0;
                        return (
                          <div key={menuItem.id} className="flex items-center gap-3 px-4 py-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-[0.875rem] font-medium truncate">{menuItem.name}</span>
                                <span className="text-[0.6875rem] text-(--color-text-secondary) shrink-0 font-semibold">
                                  ×{qty}
                                </span>
                              </div>
                              <div className="h-1.25 rounded-full bg-(--color-surface-muted) overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-(--color-text-primary)/60 transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                            <div className="text-[0.875rem] font-bold shrink-0 w-14 text-right">
                              {itemTime > 0
                                ? t`${itemTime} min`
                                : <span className="text-(--color-text-secondary) font-normal">—</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {suggestedMinutes > 0 && (
                      <p className="text-[0.75rem] text-(--color-text-secondary)">
                        <Trans>
                          Longest item: {suggestedMinutes} min — ticket fires when the last plate is ready.
                        </Trans>
                      </p>
                    )}
                  </div>
                )}

                {/* Target time */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide">
                      <Trans>Target time</Trans>
                    </Label>
                    {suggestedMinutes > 0 && suggestedMinutes === assignment.targetMinutes && (
                      <span className="text-[0.6875rem] text-(--color-text-secondary)">
                        <Trans>Matches longest item</Trans>
                      </span>
                    )}
                  </div>
                  <div className="flex items-stretch h-11 border border-(--color-border) rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => patchAssignment({ targetMinutes: Math.max(1, assignment.targetMinutes - 1) })}
                      className="w-11 bg-(--color-surface-muted) hover:bg-(--color-surface-muted)/80 flex items-center justify-center"
                    >
                      <Minus className="w-4 h-4 text-(--color-text-primary)" />
                    </button>
                    <input
                      type="number"
                      value={assignment.targetMinutes}
                      min={1}
                      max={120}
                      onChange={(e) =>
                        patchAssignment({ targetMinutes: Math.max(1, Number(e.target.value)) })
                      }
                      className="flex-1 text-center border-0 focus:ring-0 font-semibold bg-(--color-surface-content) text-(--color-text-primary) text-[0.875rem] outline-none"
                    />
                    <div className="px-3 flex items-center text-(--color-text-secondary) text-[0.8125rem] border-l border-(--color-border) bg-(--color-surface-muted)">
                      <Trans>min</Trans>
                    </div>
                    <button
                      type="button"
                      onClick={() => patchAssignment({ targetMinutes: Math.min(120, assignment.targetMinutes + 1) })}
                      className="w-11 bg-(--color-surface-muted) hover:bg-(--color-surface-muted)/80 flex items-center justify-center"
                    >
                      <Plus className="w-4 h-4 text-(--color-text-primary)" />
                    </button>
                  </div>
                </div>

                {/* Print chit */}
                <div className="flex items-center justify-between p-4 rounded-lg border border-(--color-border) bg-(--color-surface-muted)">
                  <div>
                    <div className="text-[0.875rem] font-semibold"><Trans>Print physical chit</Trans></div>
                    <div className="text-[0.75rem] text-(--color-text-secondary)">
                      <Trans>Sends a copy to the expo printer immediately.</Trans>
                    </div>
                  </div>
                  <Switch
                    checked={assignment.printChit}
                    onCheckedChange={(v) => patchAssignment({ printChit: v })}
                  />
                </div>

                {/* Notify allergen */}
                <div className="flex items-center justify-between p-4 rounded-lg border border-(--color-border) bg-(--color-surface-muted)">
                  <div>
                    <div className="text-[0.875rem] font-semibold"><Trans>Notify allergen lead</Trans></div>
                    <div className="text-[0.75rem] text-(--color-text-secondary)">
                      <Trans>Auto-pings sous-chef if any allergen tag is set.</Trans>
                    </div>
                  </div>
                  <Switch
                    checked={assignment.notifyAllergen}
                    onCheckedChange={(v) => patchAssignment({ notifyAllergen: v })}
                  />
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Right: Final review ── */}
      <aside className="lg:sticky lg:top-3">
        <div className="bg-(--color-surface-content) border border-(--color-border) rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-(--color-border)">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[0.6875rem] tracking-wider text-(--color-text-secondary) font-semibold uppercase">
                  <Trans>Ready to fire</Trans>
                </div>
                <div className="text-[1.25rem] font-extrabold"><Trans>Ticket</Trans></div>
              </div>
              <PrioPill prio={assignment.priority} />
            </div>
          </div>

          {/* Summary */}
          <div className="px-5 py-4 grid grid-cols-2 gap-y-3 gap-x-3 text-[0.8125rem] border-b border-(--color-border)">
            {[
              { label: t`Table`,  value: customer.tableName || '—' },
              { label: t`Guests`, value: customer.guestCount },
              { label: t`Chef`,   value: assignment.leadChefName || '—' },
              { label: t`Target`, value: t`${assignment.targetMinutes} min` },
            ].map(({ label, value }) => (
              <div key={label}>
                <div className="text-(--color-text-secondary) text-[0.6875rem] uppercase tracking-wider">{label}</div>
                <div className="font-semibold text-[0.875rem] mt-1">{value}</div>
              </div>
            ))}
          </div>

          {/* Items */}
          <div className="p-3 max-h-[17.5rem] overflow-y-auto">
            <CartLines lines={state.cart} onSetQty={state.setQty} compact />
          </div>

          {/* Totals */}
          <div className="px-5 py-4 border-t border-(--color-border) text-[0.8125rem] space-y-1.5">
            <div className="flex justify-between">
              <span className="text-(--color-text-secondary)"><Trans>Subtotal</Trans></span>
              <span className="font-semibold">${state.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-(--color-text-secondary)"><Trans>Tax</Trans></span>
              <span className="font-semibold">${state.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-[1rem] pt-2 mt-1 border-t border-(--color-border)">
              <span className="font-bold"><Trans>Total</Trans></span>
              <span className="font-extrabold">${state.total.toFixed(2)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="p-3 border-t border-(--color-border) flex items-center gap-2 bg-(--color-surface-muted)">
            <Button variant="outline" onClick={onBack} className="flex-1 h-11 gap-2">
              <ArrowLeft className="w-4 h-4" /><Trans>Back</Trans>
            </Button>
            <Button
              onClick={() => submitMutation.mutate()}
              disabled={submitMutation.isPending || state.cart.length === 0}
              className="flex-1 h-11 gap-2 bg-(--color-success) hover:bg-(--color-success-hover) text-(--brand-white)"
            >
              <Check className="w-4 h-4" />
              {submitMutation.isPending ? <Trans>Sending…</Trans> : <Trans>Send to line</Trans>}
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}
